/**
 * Cosmetic streak-trail palettes — pure HSL color presets, no gameplay
 * impact. Each palette gates on a mastery-rank tier from
 * site/profile.ts. The streak-curve (cyan→magenta in the legacy
 * "ribbon" palette) is generalized here to a per-palette hue sweep.
 *
 * Stored key is the palette `id`; unknown / still-locked ids fall back
 * to "ribbon" (always unlocked at Novice).
 */
import { getProfile, rankForXp } from "../site/profile";

export interface TrailPalette {
  id: string;
  name: string;
  /** HSL hue at streak 5 (the streak where the trail first lights up). */
  hueStart: number;
  /** HSL hue once the streak curve saturates (streak ≥ 30). */
  hueEnd: number;
  /** HSL saturation, 0..1. */
  sat: number;
  /** HSL lightness, 0..1. */
  lum: number;
  /** Mastery-rank name from rankForXp(). Profile must be ≥ this rank
   *  to unlock. "Novice" = always unlocked. */
  unlockRank: string;
}

/** One palette per mastery tier. Order MUST match the tier order in
 *  profile.ts RANK_THRESHOLDS so a higher-rank player sees more options
 *  than a lower-rank one. */
export const TRAIL_PALETTES: TrailPalette[] = [
  { id: "ribbon",   name: "Ribbon",        hueStart: 0.50, hueEnd: 0.82, sat: 0.9,  lum: 0.6,  unlockRank: "Novice" },
  { id: "ember",    name: "Ember",         hueStart: 0.05, hueEnd: 0.12, sat: 0.95, lum: 0.58, unlockRank: "Adept" },
  { id: "ion",      name: "Ion",           hueStart: 0.55, hueEnd: 0.70, sat: 0.85, lum: 0.62, unlockRank: "Skilled" },
  { id: "auroral",  name: "Auroral",       hueStart: 0.32, hueEnd: 0.48, sat: 0.85, lum: 0.6,  unlockRank: "Expert" },
  { id: "violet",   name: "Violet Echo",   hueStart: 0.75, hueEnd: 0.92, sat: 0.9,  lum: 0.62, unlockRank: "Master" },
  // Prism sweeps the full hue wheel; a "you finished the climb" flex.
  { id: "prism",    name: "Prism",         hueStart: 0.0,  hueEnd: 1.0,  sat: 0.95, lum: 0.62, unlockRank: "Grandmaster" },
];

const TIER_ORDER = ["Novice", "Adept", "Skilled", "Expert", "Master", "Grandmaster"];

/** True when the player's current mastery tier is at-or-above the
 *  palette's required tier. Defensive against unknown tier names. */
export function isPaletteUnlocked(p: TrailPalette, currentRank: string): boolean {
  const have = TIER_ORDER.indexOf(currentRank);
  const need = TIER_ORDER.indexOf(p.unlockRank);
  if (have < 0 || need < 0) return false;
  return have >= need;
}

/** Convenience — reads totalXp from the persisted profile, derives the
 *  rank name, returns all palettes the player has earned. */
export function unlockedPalettes(): TrailPalette[] {
  const rank = rankForXp(getProfile().totalXp).name;
  return TRAIL_PALETTES.filter((p) => isPaletteUnlocked(p, rank));
}

export function getPalette(id: string): TrailPalette {
  return TRAIL_PALETTES.find((p) => p.id === id) ?? TRAIL_PALETTES[0];
}

/** Resolve an id to a palette the player has actually unlocked. If the
 *  saved choice is locked (e.g., they cleared localStorage), falls back
 *  to "ribbon". */
export function effectivePalette(id: string): TrailPalette {
  const rank = rankForXp(getProfile().totalXp).name;
  const want = getPalette(id);
  return isPaletteUnlocked(want, rank) ? want : TRAIL_PALETTES[0];
}
